// The listening side, like the app's LiveListening/LiveAudioOutput: {"t":"listen"} now and every
// 4 s while it lasts, "unlisten" at the end; the recorder's bundles decoded with WebCodecs'
// AudioDecoder (Opus, 48 kHz mono, pre-skip 312) and played as they come. No jitter buffer: a gap
// in the connection is a gap in the sound, as in the app.

import { fromBase64 } from "../data/bytes.ts";
import type { Realtime, ServerFrame } from "../realtime/realtime.ts";
import { splitBundle } from "./liveShare.ts";

const KEEPALIVE_MS = 4_000;
const RATE = 48_000;

export function canListen(): boolean {
  return typeof AudioDecoder !== "undefined" && typeof AudioContext !== "undefined";
}

function opusHead(): Uint8Array<ArrayBuffer> {
  const head = new Uint8Array(19);
  head.set(new TextEncoder().encode("OpusHead"));
  head[8] = 1;
  head[9] = 1;
  new DataView(head.buffer).setUint16(10, 312, true);
  new DataView(head.buffer).setUint32(12, RATE, true);
  return head;
}

export class LiveListener {
  /** Milliseconds of sound decoded so far; shown, and read by tests. */
  decodedMs = 0;
  private readonly context = new AudioContext({ sampleRate: RATE });
  private readonly decoder: AudioDecoder;
  private readonly timer: ReturnType<typeof setInterval>;
  private readonly unsubscribe: () => void;
  private next = 0;
  private timestamp = 0;
  private readonly realtime: Realtime;
  private readonly conversation: string;
  private readonly recorder: string;

  constructor(realtime: Realtime, conversation: string, recorder: string) {
    this.realtime = realtime;
    this.conversation = conversation;
    this.recorder = recorder;
    this.decoder = new AudioDecoder({ output: (data) => this.play(data), error: () => undefined });
    this.decoder.configure({ codec: "opus", sampleRate: RATE, numberOfChannels: 1, description: opusHead() });
    this.unsubscribe = realtime.subscribe((frame) => this.onFrame(frame));
    const listen = () => realtime.send({ t: "listen", conversation, user: recorder });
    listen();
    this.timer = setInterval(listen, KEEPALIVE_MS);
  }

  stop() {
    clearInterval(this.timer);
    this.unsubscribe();
    this.realtime.send({ t: "unlisten", conversation: this.conversation, user: this.recorder });
    try {
      this.decoder.close();
    } catch {
      // Closed already.
    }
    void this.context.close().catch(() => undefined);
  }

  private onFrame(frame: ServerFrame) {
    if (frame.t !== "live" || frame.conversation !== this.conversation || frame.user !== this.recorder || typeof frame.packets !== "string") return;
    if (this.decoder.state !== "configured") return;
    for (const packet of splitBundle(fromBase64(frame.packets))) {
      this.decoder.decode(new EncodedAudioChunk({ type: "key", timestamp: this.timestamp, duration: 20_000, data: packet }));
      this.timestamp += 20_000;
    }
  }

  private play(data: AudioData) {
    const samples = new Float32Array(data.numberOfFrames);
    data.copyTo(samples, { planeIndex: 0, format: "f32-planar" });
    data.close();
    const buffer = this.context.createBuffer(1, samples.length, RATE);
    buffer.copyToChannel(samples, 0);
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.context.destination);
    // As soon as it comes; after a gap a little ahead, so the next pieces join without clicks.
    const at = Math.max(this.next, this.context.currentTime + 0.08);
    source.start(at);
    this.next = at + buffer.duration;
    this.decodedMs += (samples.length * 1000) / RATE;
  }
}
